/**
 * 스캔 오인식 신고 — 자료 모형 (FN-074 · SC-027).
 *
 * ── 왜 신고가 필요한가 ──────────────────────────────────────────────
 * peeka 는 **인식되는 순간 보유에 들어간다**(FN-028). 저장 버튼을 누르지 않아도,
 * 요약 화면을 떠나도 남는다. 그래서 오인식이 그대로 자산에 쌓인다.
 * 지우는 길(FN-072)만 있으면 사용자의 목록은 깨끗해지지만 **모델은 그대로다** —
 * 같은 카드를 다음에도 똑같이 틀린다. 신고는 그 고리를 잇는 자리다.
 *
 * ── 유형이 넷인 이유 ────────────────────────────────────────────────
 * 예쁘게 나누려고 넷이 아니라 **처리하는 팀·시스템이 넷이라서** 넷이다.
 * 사용자가 고른 유형이 곧 담당 큐가 된다:
 *
 *   otherCard   다른 카드예요            → 인식 모델 (학습 데이터)
 *   wrongInfo   정보가 달라요            → 카드 메타데이터 (에디션·finish·등급)
 *   oddPrice    시세가 이상해요          → 시세 소스 (PRD §5 가중치·이상치)
 *   notListed   목록에 없는 카드예요     → 카드 DB 등록
 *
 * ⚠ 유형을 늘리거나 합치려면 **큐가 먼저 바뀌어야 한다.** 화면에서 먼저 나누면
 *   어디로 보낼지 없는 표가 하나 생긴다.
 */

import { createScanReport } from "../../api/scanReport";

/** 신고 유형 — 순서가 곧 화면의 라디오 순서다 (흔한 것부터). */
export const REPORT_KINDS = ["otherCard", "wrongInfo", "oddPrice", "notListed"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/**
 * 유형 → 처리 큐. 화면에는 안 나온다 — 사용자가 알 필요가 없는 우리 쪽 사정이다.
 * 여기 적어 두는 것은 **유형을 손댈 때 큐를 같이 보게** 하려는 것이다.
 */
export const REPORT_QUEUE: Record<ReportKind, string> = {
  otherCard: "recognition-model",
  wrongInfo: "card-metadata",
  oddPrice: "price-source",
  notListed: "card-db",
};

/** '정답 카드' 를 물어보는 유형 — 다른 카드로 잘못 봤을 때뿐이다. */
export function asksForCorrection(kind: ReportKind | null): boolean {
  return kind === "otherCard";
}

export interface MisreadReport {
  /** 신고 대상 */
  cardUid: string;
  /**
   * 신고 대상의 카탈로그 일련번호. **없을 수 있다** — 매칭 인덱스(57k)가 카탈로그(13k)의
   * 상위집합이라 인식은 됐는데 카탈로그에 없는 카드가 정상적으로 존재한다. 그 카드야말로
   * '목록에 없는 카드예요' 신고의 주인공이므로, 없다고 접수를 막지 않는다.
   */
  cardSerialNo?: number;
  kind: ReportKind;
  /**
   * 사용자가 짚어 준 진짜 카드. **선택이다.**
   * ⚠ 필수로 만들지 말 것 — "뭔지 모르겠는데 틀린 건 안다"가 가장 흔한 신고인데
   *   그 신고가 통째로 막힌다. 지정된 신고는 검증 없이 반영할 수 있어 빠를 뿐이다.
   */
  correctCardUid?: string;
  correctCardSerialNo?: number;
  /** 스캔한 시각 (ISO). 어느 시세·어느 모델 판이었는지 되짚는 데 쓴다 */
  scannedAt?: string;
}

/** 보낼 수 있는 상태인가 — 유형 하나면 충분하다 (정답 카드는 선택) */
export function canSubmit(kind: ReportKind | null): boolean {
  return kind !== null;
}

/**
 * 접수 — be `POST /api/v1/user/scan/reports` (2026-09-01 배선).
 *
 * ⚠ **실패해도 던지지 않는다.** 화면은 이 결과로 완료 단으로 넘어가는데, 신고 하나가
 *   안 갔다고 사용자를 작성 화면에 붙들어 두면 같은 신고를 몇 번이고 다시 보낸다.
 *   보낸 사람이 할 수 있는 일이 없는 실패다 — 조용히 삼키고 로그만 남긴다.
 * ⚠ **사진은 보내지 않는다** (2026-09-15 정리). 신고가 열리는 자리(보유 카드·스캔 요약)에
 *   그 카드의 스캔 원본이 손에 없어서다 — 보유 카드 쪽은 촬영본이 이미 사라진 뒤라
 *   앞으로도 없고, 스캔 요약 쪽은 세션(scanSessionStore)이 이미지를 안 들고 있다.
 *   그래서 서버 계약의 attachPhoto 도 **싣지 않는다**(BE 가 없는 값을 N 으로 적는다).
 *   화면 고지도 같은 말을 한다 — 종전엔 "사진이 함께 전송돼요" 라고 반대로 적혀 있었다.
 */
export async function submitMisreadReport(report: MisreadReport): Promise<{ ok: boolean }> {
  try {
    await createScanReport({
      cardUid: report.cardUid,
      cardSerialNo: report.cardSerialNo,
      kind: report.kind,
      correctCardUid: report.correctCardUid,
      correctCardSerialNo: report.correctCardSerialNo,
      scannedAt: report.scannedAt,
    });
    return { ok: true };
  } catch (e) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[SC-027] 신고 접수 실패", { queue: REPORT_QUEUE[report.kind], error: e });
    }
    return { ok: false };
  }
}
